import { Eye, Plus, RotateCw } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { legalService, TLegalDocumentSummary, TLegalDocumentType } from "@/api/legal";
import { DOCUMENT_TYPE_LABELS, formatEffectiveDate } from "@/components/admin/legal";
import { LegalDocumentStatusBadge } from "@/components/admin/legal/LegalDocumentStatusBadge";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
} from "@/components/data-grid";
import { FilterPillsBar } from "@/components/dashboard/FilterPillsBar";
import { AdminPageLayout } from "@/components/layout/AdminPageLayout";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Button, RouteLink, useToast } from "@/components/ui";
import { extractErrorMessage } from "@/utils";

const PAGE_SIZE = 20;

type TDocumentTypeFilter = TLegalDocumentType | "all";

const FILTER_OPTIONS: { value: TDocumentTypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "terms_of_service", label: "Terms" },
  { value: "privacy_policy", label: "Privacy" },
  { value: "cookies_policy", label: "Cookies" },
];

export function LegalDocumentsListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [typeFilter, setTypeFilter] = useState<TDocumentTypeFilter>("all");
  const [reloadTick, setReloadTick] = useState(0);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [documentToActivate, setDocumentToActivate] = useState<TLegalDocumentSummary | null>(null);

  const reload = () => setReloadTick((t) => t + 1);

  // Client mode: fetch the (type-filtered) full list once; search/sort/paginate in-memory.
  // The type filter is a real query param (legalService.listDocuments) applied via deps.
  const fetcher = async (query: TDataGridQuery) => {
    const documentType = typeFilter === "all" ? undefined : typeFilter;
    const { error, data } = await legalService.listDocuments(documentType, { signal: query.signal });
    if (error) throw error;
    return data?.data ?? [];
  };

  const handleActivate = (doc: TLegalDocumentSummary) => setDocumentToActivate(doc);

  const confirmActivate = async () => {
    if (!documentToActivate) return;

    setActivatingId(documentToActivate.id);
    const { error } = await legalService.activateDocument(documentToActivate.id);
    setActivatingId(null);
    setDocumentToActivate(null);

    if (error) {
      toast({
        variant: "destructive",
        title: "Could not activate document",
        description: await extractErrorMessage(error),
      });
      return;
    }

    toast({ variant: "success", title: "Document activated" });
    reload();
  };

  const columns: TColumnDef<TLegalDocumentSummary>[] = [
    {
      id: "title",
      header: "Title",
      sortable: true,
      accessor: (doc) => doc.title ?? "",
      cell: (doc) => <span className="font-medium text-foreground">{doc.title}</span>,
    },
    {
      id: "document_type",
      header: "Type",
      sortable: true,
      accessor: (doc) => DOCUMENT_TYPE_LABELS[doc.document_type] ?? doc.document_type,
    },
    {
      id: "version",
      header: "Version",
      sortable: true,
      accessor: (doc) => doc.version ?? "",
      cell: (doc) => <span className="text-muted-foreground">v{doc.version}</span>,
    },
    {
      id: "effective_at",
      header: "Effective date",
      sortable: true,
      accessor: (doc) => doc.effective_at ?? "",
      cell: (doc) => (
        <span className="text-muted-foreground">{formatEffectiveDate(doc.effective_at)}</span>
      ),
    },
    {
      id: "is_active",
      header: "Status",
      sortable: true,
      accessor: (doc) => (doc.is_active ? 1 : 0),
      cell: (doc) => <LegalDocumentStatusBadge isActive={doc.is_active} />,
    },
  ];

  return (
    <AdminPageLayout
      title="Legal Documents"
      subtitle="Manage versioned legal templates. Create new versions and activate the one users should see."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reload}>
            <RotateCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <RouteLink to="/app/admin/legal/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create version
            </Button>
          </RouteLink>
        </div>
      }
    >
      <FilterPillsBar>
        {FILTER_OPTIONS.map((option) => (
          <Button
            key={option.value}
            size="sm"
            variant={typeFilter === option.value ? "default" : "outline"}
            onClick={() => setTypeFilter(option.value)}
            className="flex-shrink-0"
          >
            {option.label}
          </Button>
        ))}
      </FilterPillsBar>

      <DataGrid<TLegalDocumentSummary>
        getRowId={(row) => row.id}
        persistKey="admin.legal-documents"
        ariaLabel="Legal documents"
        data={{
          mode: "client",
          fetcher,
          deps: [typeFilter, reloadTick],
          searchFields: ["title", "document_type", "version"],
        }}
        columns={columns}
        pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
        toolbar={{
          search: true,
          refresh: true,
          columnVisibility: true,
          density: true,
          export: ["csv", "json"],
          searchPlaceholder: "Search by title, type, or version",
        }}
        rowActions={(doc) => [
          {
            label: "View",
            icon: <Eye className="h-4 w-4" />,
            onClick: () =>
              void navigate({
                to: "/app/admin/legal/$document_id",
                params: { document_id: doc.id },
              }),
          },
          ...(!doc.is_active
            ? [
                {
                  label: "Activate",
                  disabled: activatingId === doc.id,
                  onClick: () => handleActivate(doc),
                },
              ]
            : []),
        ]}
        emptyState={{
          title: "No legal documents yet",
          description: "Create a new version to publish terms, privacy, or cookies policies.",
          action: {
            label: "Create version",
            onClick: () => void navigate({ to: "/app/admin/legal/new" }),
          },
        }}
      />

      <ConfirmDialog
        open={documentToActivate !== null}
        onOpenChange={(open) => {
          if (!open) setDocumentToActivate(null);
        }}
        title="Activate document?"
        description={
          documentToActivate
            ? `Activate "${documentToActivate.title}" (v${documentToActivate.version})? This will deactivate other active versions of ${documentToActivate.document_type.replace(/_/g, " ")}.`
            : undefined
        }
        confirmLabel="Activate"
        variant="default"
        isConfirming={activatingId !== null}
        onConfirm={confirmActivate}
      />
    </AdminPageLayout>
  );
}
